import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';

interface Job {
  id: number;
  title: string;
  job_type: string;
  location_type: string;
  salary: string;
  is_active: boolean;
  created_at: string;
  application_count?: number;
}

const EmployerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchJobs();
    }
  }, [user]);

  const fetchJobs = async () => {
    try {
      const response = await api.get(`/api/jobs/employer/${user?.id}`);
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleJobStatus = async (jobId: number, isActive: boolean) => {
    try {
      await api.put(`/api/jobs/${jobId}`, {
        ...jobs.find(j => j.id === jobId),
        is_active: !isActive
      });
      
      setJobs(jobs.map(job => 
        job.id === jobId ? { ...job, is_active: !isActive } : job
      ));
    } catch (error) {
      console.error('Error updating job:', error);
    }
  };

  const deleteJob = async (jobId: number) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await api.delete(`/api/jobs/${jobId}`);
        setJobs(jobs.filter(job => job.id !== jobId));
      } catch (error) {
        console.error('Error deleting job:', error);
      }
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Employer Dashboard</h1>
        <p>Welcome, {user?.name}!</p>
        <Link to="/employer/post-job" className="btn btn-primary">
          Post New Job
        </Link>
      </div>

      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>Total Jobs</h3>
          <p className="stat-number">{jobs.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active Jobs</h3>
          <p className="stat-number">{jobs.filter(job => job.is_active).length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Applications</h3>
          <p className="stat-number">
            {jobs.reduce((sum, job) => sum + (job.application_count || 0), 0)}
          </p>
        </div>
      </div>

      <div className="jobs-section">
        <h2>Your Job Postings</h2>
        
        {jobs.length === 0 ? (
          <div className="empty-state">
            <p>You haven't posted any jobs yet.</p>
            <Link to="/employer/post-job" className="btn btn-primary">
              Post Your First Job
            </Link>
          </div>
        ) : (
          <div className="jobs-list">
            {jobs.map(job => (
              <div key={job.id} className="job-item">
                <div className="job-info">
                  <h3>{job.title}</h3>
                  <div className="job-meta">
                    <span className={`job-type ${job.job_type}`}>
                      {job.job_type.replace('-', ' ')}
                    </span>
                    <span className={`location-type ${job.location_type}`}>
                      {job.location_type}
                    </span>
                    {job.salary && <span className="salary">{job.salary}</span>}
                    <span className={`status ${job.is_active ? 'active' : 'inactive'}`}>
                      {job.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="job-date">Posted on {formatDate(job.created_at)}</p>
                </div>
                
                <div className="job-actions">
                  <button
                    onClick={() => toggleJobStatus(job.id, job.is_active)}
                    className={`btn ${job.is_active ? 'btn-warning' : 'btn-success'}`}
                  >
                    {job.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => deleteJob(job.id)}
                    className="btn btn-danger"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerDashboard;
